import {chunk} from '@benchling/js-utils';
import {Accordion, createStyles} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FONT_PROXIMA_13, FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_DARKER, WHITE} from 'ui-library/Colors';

import {ExpandableDetailsConfig} from '../StepConfigEditorTypes';
import ExpandableDetailsSection from './ExpandableDetailsSection';
import {getSectionNameOrNull, HelpButtonOrNull} from './SectionName';

export interface StepConfigField {
  variableName?: string;
  addIcon?: JSX.Element;
  editor: JSX.Element;
  infoText?: string;
  systemVariableName?: string;
}
interface StepConfigFieldsTemplateProps {
  sectionName?: string;
  fields: Array<StepConfigField>;
  numColumns: number;
  isCompact?: boolean;
  sectionInfoText?: string;
  isCollapsible?: boolean;
  expandableDetails?: ExpandableDetailsConfig;
}

/** @package */
export function VariableHeaderOrNull({
  variableName,
  infoText,
  addIcon,
  isCompact,
}: {
  variableName?: string;
  infoText?: string;
  addIcon?: JSX.Element;
  isCompact: boolean;
}): JSX.Element | null {
  if (variableName == null) {
    return null;
  }
  return (
    <div className={css(stepConfigFieldsTemplateStyles.variableHeader)}>
      <span
        className={css(
          !isCompact && stepConfigFieldsTemplateStyles.variableText,
          isCompact && stepConfigFieldsTemplateStyles.compactVariableText
        )}
      >
        {variableName}
      </span>
      <HelpButtonOrNull infoText={infoText} isCompact={isCompact} />
      {addIcon}
    </div>
  );
}

const useStyles = createStyles(() => ({
  content: {
    padding: 0,
  },
  chevron: {
    marginRight: 5,
  },
  control: {
    margin: 0,
    color: GRAY_DARKER,
    paddingLeft: 0,
  },
  item: {backgroundColor: WHITE, '&[data-active]': {backgroundColor: WHITE}},
}));

export function CollapsingSection({
  children,
  sectionHeader,
}: {
  children: React.ReactNode;
  sectionHeader: JSX.Element | null;
}): JSX.Element {
  const {classes} = useStyles();
  return (
    <Accordion
      classNames={{
        content: classes.content,
        chevron: classes.chevron,
        control: classes.control,
        item: classes.item,
      }}
      variant="filled"
    >
      <Accordion.Item value="section">
        <Accordion.Control>{sectionHeader}</Accordion.Control>
        <Accordion.Panel>{children}</Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

function getColumnElements(
  rowFields: Array<StepConfigField>,
  numColumns: number,
  isCompact: boolean
): Array<JSX.Element> {
  return rowFields.map(({variableName, infoText, addIcon, editor}, colIndex) => (
    <Col
      key={colIndex}
      sm={12 / numColumns}
      // For two-column displays, decrease the amount of padding between the two columns by reducing
      // the padding on the right of the first column and the padding on the left of the second column
      className={css(
        numColumns === 2 && colIndex % 2 === 0 && stepConfigFieldsTemplateStyles.smallPaddingRight,
        numColumns === 2 && colIndex % 2 === 1 && stepConfigFieldsTemplateStyles.smallPaddingLeft
      )}
    >
      <VariableHeaderOrNull
        variableName={variableName}
        infoText={infoText}
        addIcon={addIcon}
        isCompact={isCompact}
      />
      {editor}
    </Col>
  ));
}

function getRowElements(
  fields: Array<StepConfigField>,
  numColumns: number,
  isCompact: boolean,
  sectionHeaderOrNull: JSX.Element | null,
  expandableDetails?: ExpandableDetailsConfig
): Array<JSX.Element> {
  if (expandableDetails != null) {
    return [
      <Row
        key={`${sectionHeaderOrNull?.props.sectionName}-${fields[0]?.variableName}`}
        className={css(!isCompact && stepConfigFieldsTemplateStyles.marginBottom)}
      >
        <Col sm={12}>
          {sectionHeaderOrNull}
          <ExpandableDetailsSection fields={fields} expandableDetails={expandableDetails} />
        </Col>
      </Row>,
    ];
  }
  return [...chunk(fields, numColumns)].map((rowFields, rowIndex) => (
    <Row key={rowIndex} className={css(!isCompact && stepConfigFieldsTemplateStyles.marginBottom)}>
      {rowIndex === 0 && sectionHeaderOrNull && <Col sm={12}>{sectionHeaderOrNull}</Col>}
      {getColumnElements(rowFields, numColumns, isCompact)}
    </Row>
  ));
}

// TODO(BNCH-75415): Use this component to render taggedUnion editors.
export default function StepConfigFieldsTemplate({
  sectionName,
  fields,
  numColumns,
  isCompact = false,
  isCollapsible = false,
  sectionInfoText = undefined,
  expandableDetails,
}: StepConfigFieldsTemplateProps): JSX.Element {
  const sectionNameOrNull = getSectionNameOrNull({sectionName, sectionInfoText});
  if (isCollapsible) {
    return (
      <CollapsingSection sectionHeader={sectionNameOrNull}>
        <>{getRowElements(fields, numColumns, isCompact, null, expandableDetails)}</>
      </CollapsingSection>
    );
  }
  return <>{getRowElements(fields, numColumns, isCompact, sectionNameOrNull, expandableDetails)}</>;
}

/** @package */
export const stepConfigFieldsTemplateStyles = StyleSheet.create({
  variableHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 4,
  },
  variableText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  compactVariableText: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  marginBottom: {
    marginBottom: 8,
  },
  smallPaddingRight: {
    paddingRight: 8,
  },
  smallPaddingLeft: {
    paddingLeft: 8,
  },
});
